import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from 'components/ui/tooltip'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import moment from 'moment'
import { getStatusIcon } from './getStatusIcon'

interface Task {
	_id: string
	taskName: string
	completedBy: string | Date
	taskStatus?: string
	assignedTo?:
		| {
				_id?: string
				name?: string
				firstName?: string
				image?: string
		  }
		| string
		| null
	completed?: boolean
	description?: string
	dateStarted?: string | Date
	dateCompleted?: string | Date
	projectid?: string
	packageid?: string
	comments?: any[]
}

interface Props {
	tasks: Task[]
	onTaskClick?: (task: Task) => void
	onTaskDateChange?: (task: Task, newDate: string) => void
	isMobile?: boolean
}

const TaskEntry = ({ task, onClick, draggable }: { task: Task; onClick: () => void; draggable?: boolean }) => {
	const assignedTo =
		typeof task.assignedTo === 'object' && task.assignedTo !== null ? task.assignedTo : null
	const { icon: StatusIcon, color: statusColor } = getStatusIcon(task)

	const handleDragStart = (e: React.DragEvent) => {
		e.dataTransfer.setData('text/plain', task._id)
		e.dataTransfer.effectAllowed = 'move'
		// Add a slight opacity to the dragged element
		const target = e.currentTarget as HTMLElement
		requestAnimationFrame(() => {
			target.style.opacity = '0.4'
		})
	}

	const handleDragEnd = (e: React.DragEvent) => {
		const target = e.currentTarget as HTMLElement
		target.style.opacity = '1'
	}

	return (
		<TooltipProvider>
		<Tooltip>
			<TooltipTrigger asChild>
			<div
				draggable={draggable}
				onDragStart={draggable ? handleDragStart : undefined}
				onDragEnd={draggable ? handleDragEnd : undefined}
				onClick={e => {
					e.stopPropagation()
					onClick()
				}}
				className={`flex items-start gap-1 px-1 py-0.5 rounded cursor-pointer hover:bg-gray-100 transition-colors border-l-2 ${draggable ? 'cursor-grab active:cursor-grabbing' : ''}`}
				style={{ borderLeftColor: statusColor }}
			>
				<Avatar className="h-5 w-5 shrink-0 mt-px text-[10px]">
					<AvatarImage src={assignedTo?.image} alt={assignedTo?.name || 'Unassigned'} />
					<AvatarFallback className="text-[10px]">{(assignedTo?.name || 'U').charAt(0).toUpperCase()}</AvatarFallback>
				</Avatar>
				<span className="text-xs leading-snug font-medium text-gray-700 flex-1 break-words">
					{task.taskName}
				</span>
				<StatusIcon className="w-4 h-4 shrink-0 mt-px" style={{ color: statusColor }} />
			</div>
			</TooltipTrigger>
			<TooltipContent side="top">{task.taskName}</TooltipContent>
		</Tooltip>
		</TooltipProvider>
	)
}

const TasksCalendar = ({ tasks, onTaskClick, onTaskDateChange, isMobile = false }: Props) => {
	const [viewStartDay, setViewStartDay] = useState(() =>
		moment().startOf('month').startOf('week')
	)
	const [dragOverDate, setDragOverDate] = useState<string | null>(null)
	const dragCounterRef = useRef<Map<string, number>>(new Map())
	const containerRef = useRef<HTMLDivElement | null>(null)
	const wheelAccumRef = useRef(0)

	const taskMap = useMemo(() => {
		const map = new Map<string, Task>()
		tasks.forEach(t => map.set(t._id, t))
		return map
	}, [tasks])

	const handleDragOver = useCallback((e: React.DragEvent) => {
		e.preventDefault()
		e.dataTransfer.dropEffect = 'move'
	}, [])

	const handleDragEnter = useCallback((e: React.DragEvent, dayKey: string) => {
		e.preventDefault()
		const count = (dragCounterRef.current.get(dayKey) || 0) + 1
		dragCounterRef.current.set(dayKey, count)
		setDragOverDate(dayKey)
	}, [])

	const handleDragLeave = useCallback((e: React.DragEvent, dayKey: string) => {
		const count = (dragCounterRef.current.get(dayKey) || 0) - 1
		dragCounterRef.current.set(dayKey, count)
		if (count <= 0) {
			dragCounterRef.current.delete(dayKey)
			setDragOverDate(prev => (prev === dayKey ? null : prev))
		}
	}, [])

	const handleDrop = useCallback(
		(e: React.DragEvent, dayKey: string) => {
			e.preventDefault()
			setDragOverDate(null)
			dragCounterRef.current.clear()
			const taskId = e.dataTransfer.getData('text/plain')
			if (!taskId || !onTaskDateChange) return
			const task = taskMap.get(taskId)
			if (!task) return
			// Only fire if the date actually changed
			const currentDate = moment(task.completedBy).format('YYYY-MM-DD')
			if (currentDate === dayKey) return
			// Use noon UTC to prevent timezone offset from shifting the visible date
			onTaskDateChange(task, dayKey + 'T12:00:00.000Z')
		},
		[onTaskDateChange, taskMap]
	)

	// Primary month for the header label and "other-month" graying — midpoint of
	// the 6-week visible window so the label tracks the dominant visible month as
	// the user scrolls across boundaries.
	const primaryMonth = useMemo(
		() => viewStartDay.clone().add(21, 'days'),
		[viewStartDay]
	)

	const prevMonth = () => {
		wheelAccumRef.current = 0
		setViewStartDay(
			primaryMonth.clone().subtract(1, 'month').startOf('month').startOf('week')
		)
	}
	const nextMonth = () => {
		wheelAccumRef.current = 0
		setViewStartDay(
			primaryMonth.clone().add(1, 'month').startOf('month').startOf('week')
		)
	}
	const goToToday = () => {
		wheelAccumRef.current = 0
		setViewStartDay(moment().startOf('month').startOf('week'))
	}

	// Build array of 42 calendar days (6 weeks) starting at viewStartDay (a Sunday).
	const calendarDays = useMemo(() => {
		const days: moment.Moment[] = []
		for (let i = 0; i < 42; i++) {
			days.push(viewStartDay.clone().add(i, 'days'))
		}
		return days
	}, [viewStartDay])

	// Native non-passive wheel listener: scroll-down (deltaY > 0) advances by one
	// week, scroll-up reverses. Synthetic onWheel can't reliably preventDefault.
	useEffect(() => {
		const el = containerRef.current
		if (!el) return
		const onWheel = (e: WheelEvent) => {
			if (dragOverDate !== null) return
			if (Math.abs(e.deltaY) < Math.abs(e.deltaX)) return
			e.preventDefault()
			wheelAccumRef.current += e.deltaY
			const THRESHOLD = 80
			if (Math.abs(wheelAccumRef.current) >= THRESHOLD) {
				const weeks = Math.trunc(wheelAccumRef.current / THRESHOLD)
				wheelAccumRef.current -= weeks * THRESHOLD
				setViewStartDay(prev => prev.clone().add(weeks * 7, 'days'))
			}
		}
		el.addEventListener('wheel', onWheel, { passive: false })
		return () => el.removeEventListener('wheel', onWheel)
	}, [dragOverDate])

	// Group tasks by due date
	const tasksByDate = useMemo(() => {
		const map = new Map<string, Task[]>()
		tasks
			.filter(t => t.completedBy && moment(t.completedBy).isValid())
			.forEach(task => {
				const key = moment(task.completedBy).format('YYYY-MM-DD')
				if (!map.has(key)) map.set(key, [])
				map.get(key)!.push(task)
			})
		return map
	}, [tasks])

	const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
	const today = moment()

	return (
		<div ref={containerRef} className="bg-white border border-[rgba(0,128,254,0.25)] border-width-1px rounded-lg overflow-hidden">
			{/* Header with month navigation */}
			<div className="flex items-center justify-between px-4 py-3 bg-gray-100 border-b">
				<button
					type="button"
					onClick={prevMonth}
					className="p-1.5 hover:bg-gray-200 rounded-full transition-colors"
					title="Previous month"
				>
					<ChevronLeft className="w-5 h-5 text-gray-600" />
				</button>
				<div className="flex items-center gap-3">
					<h3 className="text-sm font-semibold text-blue-500">
						{primaryMonth.format('MMMM YYYY')}
					</h3>
					{!primaryMonth.isSame(today, 'month') && (
						<button
							type="button"
							onClick={goToToday}
							className="text-[10px] font-medium text-[#0080fe] hover:underline"
						>
							Today
						</button>
					)}
				</div>
				<button
					type="button"
					onClick={nextMonth}
					className="p-1.5 hover:bg-gray-200 rounded-full transition-colors"
					title="Next month"
				>
					<ChevronRight className="w-5 h-5 text-gray-600" />
				</button>
			</div>

			{/* Weekday headers */}
			<div className="grid grid-cols-7 border-b">
				{weekdays.map(d => (
					<div
						key={d}
						className="py-2 text-center text-xs font-medium text-gray-500 border-r last:border-r-0"
					>
						{isMobile ? d.charAt(0) : d}
					</div>
				))}
			</div>

			{/* Day cells */}
			<div className="grid grid-cols-7">
				{calendarDays.map((day, idx) => {
					const isCurrentMonth = day.isSame(primaryMonth, 'month')
					const isToday = day.isSame(today, 'day')
					const dayKey = day.format('YYYY-MM-DD')
					const dayTasks = tasksByDate.get(dayKey) || []
					const isDragOver = dragOverDate === dayKey

					return (
						<div
							key={idx}
							onDragOver={onTaskDateChange ? handleDragOver : undefined}
							onDragEnter={onTaskDateChange ? (e) => handleDragEnter(e, dayKey) : undefined}
							onDragLeave={onTaskDateChange ? (e) => handleDragLeave(e, dayKey) : undefined}
							onDrop={onTaskDateChange ? (e) => handleDrop(e, dayKey) : undefined}
							className={`
								${isMobile ? 'min-h-[50px]' : 'min-h-[100px]'} border-r border-b p-1 transition-colors
								${isCurrentMonth ? 'bg-white' : 'bg-gray-50'}
								${idx % 7 === 6 ? 'border-r-0' : ''}
								${isDragOver ? 'bg-blue-50 ring-2 ring-inset ring-[#0080fe]/40' : ''}
							`}
						>
							{/* Date number */}
							<div className="flex justify-end mb-0.5">
								<span
									className={`
										text-xs font-medium px-1
										${
											isToday
												? 'bg-[#0080fe] text-white rounded-full w-6 h-6 flex items-center justify-center'
												: ''
										}
										${isCurrentMonth ? 'text-gray-700' : 'text-gray-400'}
									`}
								>
									{day.date()}
								</span>
							</div>

							{/* Tasks */}
							{isMobile ? (
								// Mobile: colored dots only
								<div className="flex flex-wrap gap-0.5">
									{dayTasks.map(task => {
										const { color } = getStatusIcon(task)
										return (
											<TooltipProvider key={task._id}>
												<Tooltip>
													<TooltipTrigger asChild>
														<div
															draggable={!!onTaskDateChange}
															onDragStart={onTaskDateChange ? (e) => {
																e.dataTransfer.setData('text/plain', task._id)
																e.dataTransfer.effectAllowed = 'move'
															} : undefined}
															onClick={() => onTaskClick?.(task)}
															className="w-2.5 h-2.5 rounded-full cursor-pointer hover:scale-125 transition-transform"
															style={{ backgroundColor: color }}
														/>
													</TooltipTrigger>
													<TooltipContent>{task.taskName}</TooltipContent>
												</Tooltip>
											</TooltipProvider>
										)
									})}
								</div>
							) : (
								// Desktop: full task entries
								<div className="space-y-0.5">
									{dayTasks.map(task => (
										<TaskEntry
											key={task._id}
											task={task}
											onClick={() => onTaskClick?.(task)}
											draggable={!!onTaskDateChange}
										/>
									))}
								</div>
							)}
						</div>
					)
				})}
			</div>
		</div>
	)
}

export default TasksCalendar
